import {
  CashOutline,
  CheckmarkCircleOutline,
  TrendingUpOutline,
  WalletOutline,
  TimeOutline,
} from "@raresail/react-ionicons";
import { useEffect } from "react";
import styled from "styled-components";
import useGet from "../../../hooks/common/useGet";
import { API_DASHBOARD_INVESTOR } from "../../../constants/apis";
import { currencyFormat } from "../../../utils/helpers";
import { SimpleLoader } from "../../common/Loader";
import { Heading } from "../../elements/Elements";
import { primary5, PRIMARY_COLOR } from "../../../constants/colors";
import { alpha } from "@mui/material";
import { Link } from "react-router-dom";
import { INVESTMENTS } from "../../../constants/constants";
import { getListRoute } from "../../../utils/helpers";

export const InvestorDashboardCard = () => {
  const { loadData, data, loading } = useGet();

  useEffect(() => {
    loadData({ api: API_DASHBOARD_INVESTOR });
  }, []);

  if (loading) return <SimpleLoader />;
  if (!data) return null;

  const {
    total_invested = 0,
    total_expected_returns = 0,
    total_received = 0,
    total_earned = 0,
    active_portfolio = { count: 0, amount_invested: 0, expected_returns: 0 },
    completed_portfolio = { count: 0, amount_invested: 0, received: 0 },
    defaulted_portfolio = { count: 0, amount_invested: 0 },
  } = data;

  return (
    <div
      className="r15 whiteBg flexColumn hidden"
      style={{ border: `1px solid ${primary5}` }}
    >
      {/* Header */}
      <div style={{ padding: "16px 18px 0" }}>
        <div className="flexSpaceCenter">
          <Heading className="font14">Portfolio Summary</Heading>
          <Link
            to={getListRoute(INVESTMENTS)}
            className="font12 main semiBold"
          >
            View All
          </Link>
        </div>
      </div>

      {/* Counts row — by loan lifecycle (per-investor counts, not loan-wide) */}
      <div className="flex gap10" style={{ padding: "14px 18px", flexWrap: "wrap" }}>
        <CountChip
          icon={TimeOutline}
          label="Active"
          value={active_portfolio.count}
          accent={PRIMARY_COLOR}
        />
        <CountChip
          icon={CheckmarkCircleOutline}
          label="Completed"
          value={completed_portfolio.count}
          accent="#2e7d32"
        />
        {defaulted_portfolio.count > 0 && (
          <CountChip
            icon={CashOutline}
            label="Defaulted"
            value={defaulted_portfolio.count}
            accent="#c62828"
          />
        )}
      </div>

      {/* Stat rows — totals are YOUR slice only, never loan-wide */}
      <div className="flexColumn">
        <StatRow
          icon={WalletOutline}
          label="Active Principal"
          value={currencyFormat(active_portfolio.amount_invested)}
          accent={PRIMARY_COLOR}
        />
        <StatRow
          icon={TrendingUpOutline}
          label="Expected from Active"
          value={currencyFormat(active_portfolio.expected_returns)}
          accent="#e65100"
        />
        <StatRow
          icon={WalletOutline}
          label="Total Received"
          value={currencyFormat(total_received)}
          accent="#2e7d32"
        />
        <StatRow
          icon={CashOutline}
          label="Total Earned"
          value={currencyFormat(total_earned)}
          accent="#6a1b9a"
          isLast
        />
      </div>
    </div>
  );
};

// ─── Count chip ─────────────────────────────────────────────────
const CountChip = ({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: any;
  label: string;
  value: number;
  accent: string;
}) => (
  <div
    className="flexNullCenter gap10 r15 flexGrow"
    style={{ padding: "14px 16px", background: primary5 }}
  >
    <div
      className="r20 flexCenter"
      style={{
        width: 34,
        height: 34,
        background: alpha(accent, 0.1),
        color: accent,
        flexShrink: 0,
      }}
    >
      <Icon fontSize={15} />
    </div>
    <div>
      <div className="font18 bold textDark">{value}</div>
      <div className="font11 grey">{label}</div>
    </div>
  </div>
);

// ─── Stat row ───────────────────────────────────────────────────
const StatRow = ({
  icon: Icon,
  label,
  value,
  accent,
  isLast,
}: {
  icon: any;
  label: string;
  value: string;
  accent: string;
  isLast?: boolean;
}) => (
  <StatRowWrapper className="flexNullCenter gap15" isLast={isLast}>
    <div
      className="r20 flexCenter"
      style={{
        width: 36,
        height: 36,
        background: alpha(accent, 0.08),
        color: accent,
        flexShrink: 0,
      }}
    >
      <Icon fontSize={16} />
    </div>
    <div className="flexGrow">
      <div className="font12 grey">{label}</div>
    </div>
    <div className="font14 semiBold textDark">{value}</div>
  </StatRowWrapper>
);

const StatRowWrapper = styled.div<{ isLast?: boolean }>`
  padding: 14px 18px;
  border-bottom: ${({ isLast }) =>
    isLast ? "none" : `1px solid ${primary5}`};
`;